import React, { useEffect } from 'react'

/**
 * Accelerates image loading on the static-HTML pages without changing
 * any visible markup:
 *
 *  - Adds `decoding="async"` so the main thread doesn't stall waiting
 *    on image decode (Safari/Chromium both honour this).
 *  - Above-the-fold images get `loading="eager"` + `fetchpriority="high"`.
 *  - Images far below the viewport get `loading="lazy"` so the network
 *    can spend bandwidth on what the user is looking at first.
 *  - The Pentabay logo + hero icons get explicit `<link rel="preload">`
 *    tags injected into <head>, so they start fetching during HTML parse
 *    before React even mounts.
 *  - A MutationObserver re-applies these hints to any image inserted
 *    after the initial mount (SPA route swaps, dynamic galleries).
 *  - Each <img> also gains a "fade-in once loaded" affordance so a
 *    half-decoded image never flashes a blank box.
 */

// Only the header logo belongs here. A preload that nothing paints within
// a few seconds earns a console warning and competes for bandwidth with
// what the user is actually looking at, so this list stays deliberately
// short: pentabay-mark.png is only ever a favicon / apple-touch-icon / OG
// image plus one loading="lazy" brand mark, and ca-us-flags.png is not
// referenced anywhere in the app at all.
const PRELOAD = [
  '/assets/pentabay-logo-light.png',
  '/assets/pentabay-logo-dark.png',
]

export default function ImageTuner() {
  useEffect(() => {
    // 1) Preload key assets early.
    const preloads = []
    for (const href of PRELOAD) {
      // Only inject if not already preloaded.
      const existing = document.head.querySelector(
        `link[rel="preload"][href="${href}"]`
      )
      if (existing) continue
      const l = document.createElement('link')
      l.rel = 'preload'
      l.as = 'image'
      l.href = href
      l.setAttribute('data-pb-tuner', '')
      document.head.appendChild(l)
      preloads.push(l)
    }

    // No fade-in CSS - earlier versions hid every image at opacity
    // 0.001 until a `load` event flipped them visible, but that proved
    // fragile: when the source's hash-router removed and re-added the
    // hero flag <img>, the new node sometimes never fired `load` (the
    // bytes were already cached) and stayed invisible. We now rely
    // only on the safe perf hints below - no risk of an image staying
    // hidden.

    function tune(img) {
      if (img.hasAttribute('data-pb-tuning')) return
      img.setAttribute('data-pb-tuning', '')
      if (!img.hasAttribute('decoding')) img.setAttribute('decoding', 'async')

      // Viewport-aware priority.
      const rect = img.getBoundingClientRect()
      const vh = window.innerHeight
      const aboveFold = rect.top < vh * 1.5 && rect.bottom > -vh * 0.5
      if (aboveFold) {
        if (!img.getAttribute('loading')) img.setAttribute('loading', 'eager')
        img.setAttribute('fetchpriority', 'high')
      } else {
        if (!img.getAttribute('loading')) img.setAttribute('loading', 'lazy')
      }
    }

    function scan() {
      document.querySelectorAll('img').forEach(tune)
    }
    scan()

    const mo = new MutationObserver((muts) => {
      for (const m of muts) {
        m.addedNodes.forEach((n) => {
          if (n.nodeType !== 1) return
          if (n.tagName === 'IMG') tune(n)
          else n.querySelectorAll && n.querySelectorAll('img').forEach(tune)
        })
      }
    })
    mo.observe(document.body, { childList: true, subtree: true })

    return () => {
      mo.disconnect()
      preloads.forEach((l) => l.remove())
    }
  }, [])

  return null
}
